/**
 * Buchhaltungsvorbereitung.
 *
 * Der Arbeitsbereich selbst ist noch nicht gebaut. Was seit v12 schon hier
 * steht, sind die offenen Punkte aus sevDesk: Rechnungen ohne Auftragsbezug
 * und Kunden ohne Kontakt. Hier arbeitet Eveline ohnehin monatlich, und genau
 * hier fällt eine fehlende Zuordnung auf – nicht in einer eigenen Liste im
 * Menü, die man aufrufen müsste, um zu merken, dass etwas fehlt.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import type { SevdeskStand } from '../typen';
import './buchhaltung.css';
import { Menueknopf } from '../komponenten/Shell';

export function Buchhaltungsvorbereitung() {
  const navigate = useNavigate();

  const abfrage = useQuery({
    /* Ohne Verbindungspruefung: hier zaehlen nur die zwei Zahlen. */
    queryKey: ['sevdesk-stand'],
    queryFn: () => api.hole<SevdeskStand>('/sevdesk/stand'),
    staleTime: 30_000,
  });
  const d = abfrage.data;

  return (
    <div className="eh-inhalt">
      <div className="eh-kopfzeile">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
            <div className="eh-kicker">Buchhaltung</div>
            <h1 className="eh-titel">Buchhaltungsvorbereitung</h1>
          </div>
        </div>
      </div>

      {/*
        Die offenen Punkte stehen oben, nicht unter dem Platzhalter: Sie sind
        das Einzige, was diese Maske heute schon beantwortet.
      */}
      <section className="bv-offen">
        <div className="bv-offen-titel">Aus sevDesk noch offen</div>

        {!d ? (
          <div className="eh-hinweis">Wird geladen …</div>
        ) : !d.lesbar ? (
          <div className="eh-hinweisblock">
            sevDesk ist nicht angebunden – ohne Token kann Kora weder Rechnungen lesen noch
            Kontakte nachschlagen. Die Anbindung steht im Admin Center.
          </div>
        ) : (
          <div className="bv-zeilen">
            <button
              type="button"
              className={`bv-zeile ${d.rechnungenOhneAuftrag ? 'offen' : 'erledigt'}`}
              onClick={() => navigate('/abwicklung')}
            >
              <span className="bv-zahl">{d.rechnungenOhneAuftrag}</span>
              <span className="bv-text">
                {d.rechnungenOhneAuftrag === 1
                  ? 'Rechnung ohne Auftragsbezug'
                  : 'Rechnungen ohne Auftragsbezug'}
                <span className="bv-wo">
                  {d.rechnungenOhneAuftrag === 0
                    ? 'nichts offen'
                    : 'in der Auftragsabwicklung zuordnen'}
                </span>
              </span>
            </button>

            <button
              type="button"
              className={`bv-zeile ${d.ohneKontakt ? 'offen' : 'erledigt'}`}
              onClick={() => navigate('/kunden?ohneSevdesk=1')}
            >
              <span className="bv-zahl">{d.ohneKontakt}</span>
              <span className="bv-text">
                {d.ohneKontakt === 1 ? 'Kunde ohne sevDesk-Kontakt' : 'Kunden ohne sevDesk-Kontakt'}
                <span className="bv-wo">
                  {d.ohneKontakt === 0 ? 'nichts offen' : 'im Kundenstamm zuordnen'}
                </span>
              </span>
            </button>
          </div>
        )}
      </section>

      <div className="eh-karte bv-platzhalter">
        <p className="eh-hinweis">
          Der übrige Bereich ist vorbereitet, aber noch nicht gebaut. Der Zugang ist bereits
          geregelt: freigeschaltet sind Eveline und Jana, die Geschäftsführung sieht ihn ohnehin.
        </p>
        <p className="eh-hinweis">
          Was hier entstehen soll, entscheidet ihr – über{' '}
          <strong>Verbesserungsvorschläge</strong> lässt sich das festhalten, damit es nicht in
          einem Gespräch verloren geht.
        </p>
      </div>
    </div>
  );
}
